"use client";

import Link from "next/link";
import dynamic from "next/dynamic";
import { useState } from "react";
import {
  SignInButton,
  SignUpButton,
  SignedIn,
  SignedOut,
} from "@clerk/nextjs";
import { Button } from "@/components/shared/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/shared/ui/sheet";
import { Menu } from "lucide-react";

// UserButton을 클라이언트 사이드에서만 렌더링하여 hydration 오류 방지
const UserButton = dynamic(
  () => import("@clerk/nextjs").then((mod) => mod.UserButton),
  { ssr: false }
);

/**
 * @file LandingHeader.tsx
 * @description 랜딩 페이지 및 마케팅 페이지용 헤더 컴포넌트
 *
 * 용도: 랜딩 페이지 (`/`, `/brand-story`) 등 마케팅 페이지
 * 디자인: fixed, 투명 배경, 심플한 구성
 *
 * 주요 기능:
 * - 로고 링크
 * - 로그인/회원가입 버튼 (SignedOut)
 * - 대시보드 이동 버튼 + UserButton (SignedIn)
 */

export function LandingHeader() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <>
      <header className="fixed top-0 z-50 w-full bg-white/90 dark:bg-zinc-900/90 backdrop-blur-sm border-b border-zinc-100 dark:border-zinc-800 transition-all duration-300">
        <div className="mx-auto flex h-16 max-w-screen-2xl items-center justify-between px-4 sm:px-6">
          <Link
            href="/"
            className="font-black text-xl sm:text-2xl tracking-tighter text-zinc-900 dark:text-zinc-100 shrink-0"
          >
            BIDIX
          </Link>

          {/* 데스크톱 메뉴 */}
          <div className="hidden md:flex items-center gap-5">
            <Link href="/list" className="text-sm font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition">
              경매 찾기
            </Link>
            <Link href="/features" className="text-sm font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition">
              기능 소개
            </Link>
            <Link href="/guide" className="text-sm font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition">
              이용 안내
            </Link>
            <SignedOut>
              <SignInButton mode="modal">
                <button className="text-sm font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition">
                  로그인
                </button>
              </SignInButton>
              <SignUpButton mode="modal">
                <Button className="bg-zinc-900 dark:bg-zinc-100 hover:bg-black dark:hover:bg-zinc-200 text-white dark:text-zinc-900 rounded-full px-5 h-9 text-sm font-bold transition-all duration-200 hover:scale-105 hover:shadow-lg active:scale-95">
                  경매 찾기 시작
                </Button>
              </SignUpButton>
            </SignedOut>

            <SignedIn>
              <Link href="/dashboard">
                <Button
                  variant="ghost"
                  className="text-zinc-900 dark:text-zinc-100 hover:text-black dark:hover:text-white font-medium"
                >
                  내 검토 기록
                </Button>
              </Link>

              <UserButton
                afterSignOutUrl="/"
                appearance={{
                  elements: { avatarBox: "h-8 w-8" },
                }}
              />
            </SignedIn>
          </div>

          {/* 모바일 메뉴 */}
          <div className="flex md:hidden items-center gap-2">
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-zinc-900 dark:text-zinc-100"
                >
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">메뉴 열기</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] sm:w-[320px]">
                <SheetHeader>
                  <SheetTitle className="text-left">메뉴</SheetTitle>
                </SheetHeader>
                <div className="mt-6 flex flex-col gap-4">
                  <Link
                    href="/list"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-left font-medium"
                    >
                      경매 찾기
                    </Button>
                  </Link>
                  <Link
                    href="/features"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-left font-medium"
                    >
                      기능 소개
                    </Button>
                  </Link>
                  <Link
                    href="/guide"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-left font-medium"
                    >
                      이용 안내
                    </Button>
                  </Link>
                  <SignedOut>
                    <SignInButton mode="modal">
                      <Button
                        variant="ghost"
                        className="w-full justify-start text-left font-medium"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        로그인
                      </Button>
                    </SignInButton>
                    <SignUpButton mode="modal">
                      <Button
                        className="w-full bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 font-bold"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        경매 찾기 시작
                      </Button>
                    </SignUpButton>
                  </SignedOut>

                  <SignedIn>
                    <Link
                      href="/dashboard"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Button
                        variant="ghost"
                        className="w-full justify-start text-left font-medium"
                      >
                        내 검토 기록
                      </Button>
                    </Link>

                    <div className="pt-4 border-t border-zinc-200 dark:border-zinc-800">
                      <div className="flex justify-center">
                        <UserButton
                          afterSignOutUrl="/"
                          appearance={{
                            elements: { avatarBox: "h-10 w-10" },
                          }}
                        />
                      </div>
                    </div>
                  </SignedIn>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
    </>
  );
}
